import React, { useState, useMemo, useLayoutEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { Page } from "../types/Page";
import "./ApiDocsPage.css";

// Multi-language code examples
interface CodeExamples {
  python: string;
  curl?: string;
  typescript?: string;
}

interface Parameter {
  name: string;
  type: string;
  required: boolean;
  default?: string;
  description: string;
}

interface DocMethod {
  id: string;
  name: string;
  httpMethod?: "GET" | "POST" | "PATCH" | "DELETE";
  endpoint?: string;
  signature: string;
  description: string;
  tip?: string;
  parameters?: Parameter[];
  returns?: string;
  responseExample?: string;
  examples: CodeExamples;
}

interface DocSection {
  id: string;
  category: "GET STARTED" | "CLINICAL WORKFLOWS" | "SDK REFERENCE" | "SPECS & GUIDES";
  name: string;
  description: string;
  badge?: string;
  methods: DocMethod[];
}

// Copy to clipboard button
const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button onClick={handleCopy} className="docs-copy-btn" title="Copy code">
      {copied ? (
        <>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span className="copied-text">Copied</span>
        </>
      ) : (
        <>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
          <span>Copy</span>
        </>
      )}
    </button>
  );
};

// Documentation Sections Data (Clean developer reference, zero emojis)
const docSections: DocSection[] = [
  {
    id: "overview",
    category: "GET STARTED",
    name: "Overview & Architecture",
    description: "Cynaps independent validation infrastructure and core SDK architecture.",
    methods: [
      {
        id: "install_sdk",
        name: "SDK Installation",
        signature: "pip install cynaps-sdk",
        description: "Install the official Cynaps Python SDK from PyPI. Compatible with Python 3.8+.",
        examples: {
          python: `# Install with pip
pip install cynaps-sdk

# Or with Poetry
poetry add cynaps-sdk

# Verify installation
python -c "import cynaps; print(cynaps.__version__)"`,
          curl: `# Cynaps REST API is accessible directly over HTTPS
curl -X GET "https://app.cynaps.io/api/health" \\
  -H "Authorization: Token sk_live_xxxx"`,
          typescript: `// Install via npm or yarn
npm install @cynaps/sdk
// or
yarn add @cynaps/sdk`,
        },
      },
      {
        id: "architecture_flow",
        name: "Core Platform Flow",
        signature: "Storage Connector → Pre-labeling → Multi-Reviewer Consensus → Export",
        description:
          "Cynaps orchestrates data from your storage, applies pre-annotations via neural networks, distributes tasks to domain experts, and verifies multi-reviewer consensus before delivering audit-ready validated results.",
        tip: "Raw patient DICOM data never leaves your secure infrastructure when using presigned cloud connectors (AWS S3, GCP GCS, Azure Blob).",
        examples: {
          python: `import cynaps

# 1. Initialize client with your API key
client = cynaps.Client(api_key="sk_live_your_key_here")

# 2. Check current workspace and active credits
user = client.users.get_current()
print(f"Logged in as: {user.email}")
print(f"Available Balance: {client.billing.get_balance()} Credits")`,
          curl: `curl -X GET "https://app.cynaps.io/api/current-user/whoami" \\
  -H "Authorization: Token sk_live_your_key_here"`,
          typescript: `import { CynapsClient } from '@cynaps/sdk';

const client = new CynapsClient({ apiKey: 'sk_live_your_key_here' });
const user = await client.users.getCurrent();
console.log(\`Logged in as \${user.email}\`);`,
        },
      },
    ],
  },
  {
    id: "quickstart",
    category: "GET STARTED",
    name: "Quickstart",
    description: "Launch a validation project and export reviewed results programmatically.",
    badge: "Start Here",
    methods: [
      {
        id: "end_to_end_quickstart",
        name: "Complete Project Lifecycle",
        signature: "client.projects.create() → upload() → export()",
        description: "Full end-to-end Python script to create a project, ingest imaging scans, pay deposit, and export masks.",
        examples: {
          python: `import cynaps

# Initialize authenticated client
client = cynaps.Client(api_key="sk_live_xxxx")

# 1. Create a thoracic CT tumor detection project
project = client.projects.create(
    name="Lung Nodule CT Segmentation",
    annotation_type="polygon",
    labels=["Nodule A", "Lobe RUL", "Lobe RML", "Lobe RLL"]
)

# 2. Ingest DICOM CT series from cloud bucket
project.upload_from_s3(
    bucket="hospital-imaging-archive",
    prefix="ct_scans/2026/cohort_a/",
    file_filter="*.dcm"
)

# 3. Fund project security deposit with platform credits
project.pay_deposit(payment_method="credits")

# 4. Wait for consensus reviews from board-certified radiologists
print("Waiting for doctor consensus...")
project.wait_for_completion(poll_interval=10)

# 5. Export FDA-grade COCO/NIfTI segmentation masks
annotations = project.export(format="coco")
print(f"Generated {len(annotations)} clinical masks.")`,
          curl: `curl -X POST "https://app.cynaps.io/api/projects" \\
  -H "Authorization: Token sk_live_xxxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "Lung Nodule CT Segmentation",
    "label_config": "<View><Image name=\\"image\\" value=\\"$image\\"/><PolygonLabels name=\\"label\\" toName=\\"image\\"><Label value=\\"Nodule\\" background=\\"#ff0000\\"/></PolygonLabels></View>"
  }'`,
          typescript: `import { CynapsClient } from '@cynaps/sdk';

const client = new CynapsClient({ apiKey: 'sk_live_xxxx' });

const project = await client.projects.create({
  title: 'Lung Nodule CT Segmentation',
  annotationType: 'polygon',
  labels: ['Nodule A', 'Lobe RUL']
});

console.log(\`Project created with ID: \${project.id}\`);`,
        },
      },
    ],
  },
  {
    id: "authentication",
    category: "GET STARTED",
    name: "Authentication",
    description: "API keys, cryptographic tokens, and role-based permissions.",
    methods: [
      {
        id: "auth_keys",
        name: "API Key Management",
        signature: "Authorization: Token sk_live_xxxx",
        description:
          "All API requests must include your API token in the Authorization header. Production keys start with 'sk_live_' and test sandbox keys start with 'sk_test_'.",
        tip: "Set your API key as the CYNAPS_API_KEY environment variable to avoid hardcoding credentials in source code.",
        examples: {
          python: `import os
import cynaps

# Reads CYNAPS_API_KEY from environment variables
client = cynaps.Client()

# Or pass explicitly
client = cynaps.Client(api_key=os.getenv("CYNAPS_API_KEY"))`,
          curl: `export CYNAPS_API_KEY="sk_live_xxxx"

curl -X GET "https://app.cynaps.io/api/projects" \\
  -H "Authorization: Token $CYNAPS_API_KEY"`,
          typescript: `const client = new CynapsClient({
  apiKey: process.env.CYNAPS_API_KEY
});`,
        },
      },
    ],
  },
  {
    id: "dicom_workflows",
    category: "CLINICAL WORKFLOWS",
    name: "DICOM & PACS Ingestion",
    description: "Multi-planar reconstruction, slice thickness calibration, and secure presigned streaming.",
    methods: [
      {
        id: "dicom_upload",
        name: "import_dicom_series",
        signature: "project.import_dicom_series(folder_path, modalities=['CT', 'MR'])",
        description: "Upload DICOM series directly or sync with PACS archives. Automatically parses window level/width and orientation matrices.",
        parameters: [
          { name: "folder_path", type: "str", required: true, description: "Local directory containing .dcm slices" },
          { name: "modalities", type: "list[str]", required: false, default: "['CT', 'MR']", description: "Modalities to filter and parse" },
          { name: "deidentify", type: "bool", required: false, default: "True", description: "Automatically redact patient PHI metadata" },
        ],
        returns: "Dict with ingested task counts and series UID maps",
        examples: {
          python: `# Ingest local or mounted PACS folder
result = project.import_dicom_series(
    folder_path="./patient_scans/series_001/",
    modalities=["CT", "MR"],
    deidentify=True
)

print(f"Ingested {result['slices_count']} slices across {result['series_count']} series")`,
          curl: `curl -X POST "https://app.cynaps.io/api/projects/123/import-dicom" \\
  -H "Authorization: Token sk_live_xxxx" \\
  -F "file=@/path/to/scan.dcm" \\
  -F "deidentify=true"`,
        },
      },
    ],
  },
  {
    id: "consensus_review",
    category: "CLINICAL WORKFLOWS",
    name: "Consensus & MD Reviews",
    description: "Multi-doctor cross-validation, intersection-over-union thresholds, and expert resolution.",
    methods: [
      {
        id: "consensus_config",
        name: "configure_consensus_rules",
        signature: "project.set_consensus(min_reviews=2, iou_threshold=0.85, expert_escalation=True)",
        description: "Set clinical consensus parameters to guarantee FDA-ready label quality across independent annotators.",
        parameters: [
          { name: "min_reviews", type: "int", required: true, description: "Number of independent doctors reviewing each scan" },
          { name: "iou_threshold", type: "float", required: true, description: "Minimum IoU overlap required for automatic approval (0.0 - 1.0)" },
          { name: "expert_escalation", type: "bool", required: false, default: "True", description: "Route conflicting annotations to Senior Radiologists" },
        ],
        returns: "ConsensusRuleConfig object",
        examples: {
          python: `# Enforce strict dual-doctor consensus with expert escalation
project.set_consensus(
    min_reviews=2,
    iou_threshold=0.85,
    expert_escalation=True
)`,
          curl: `curl -X PATCH "https://app.cynaps.io/api/projects/123/consensus" \\
  -H "Authorization: Token sk_live_xxxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "min_reviews": 2,
    "iou_threshold": 0.85,
    "expert_escalation": true
  }'`,
        },
      },
    ],
  },
  {
    id: "projects_sdk",
    category: "SDK REFERENCE",
    name: "Projects API",
    description: "Create, configure, monitor, and manage clinical annotation workspaces.",
    methods: [
      {
        id: "projects_list",
        name: "client.projects.list",
        httpMethod: "GET",
        endpoint: "/api/projects",
        signature: "client.projects.list(page=1, page_size=20)",
        description: "Retrieve a paginated list of all projects in your organization.",
        parameters: [
          { name: "page", type: "int", required: false, default: "1", description: "Page number" },
          { name: "page_size", type: "int", required: false, default: "20", description: "Number of items per page (max 100)" },
        ],
        returns: "SyncPager[Project]",
        responseExample: `{
  "count": 12,
  "results": [
    {
      "id": 123,
      "title": "Brain MRI Glioblastoma Segmentation",
      "task_number": 450,
      "completed_task_number": 412,
      "created_at": "2026-08-20T10:15:30Z"
    }
  ]
}`,
        examples: {
          python: `# List all projects
for project in client.projects.list():
    print(f"[{project.id}] {project.title} - {project.completed_task_number}/{project.task_number} done")`,
          curl: `curl -X GET "https://app.cynaps.io/api/projects?page=1&page_size=20" \\
  -H "Authorization: Token sk_live_xxxx"`,
        },
      },
      {
        id: "projects_create",
        name: "client.projects.create",
        httpMethod: "POST",
        endpoint: "/api/projects",
        signature: "client.projects.create(name, annotation_type, labels)",
        description: "Create a new annotation project with specified labels and clinical schema.",
        parameters: [
          { name: "name", type: "str", required: true, description: "Name of the project" },
          { name: "annotation_type", type: "str", required: true, description: "'polygon', 'bounding_box', 'classification', 'ner'" },
          { name: "labels", type: "list[str]", required: true, description: "List of label class names" },
        ],
        returns: "Project instance",
        examples: {
          python: `project = client.projects.create(
    name="Chest X-Ray Pneumonia Detection",
    annotation_type="bounding_box",
    labels=["Bacterial Pneumonia", "Viral Pneumonia", "Normal"]
)
print(f"Created project: {project.id}")`,
          curl: `curl -X POST "https://app.cynaps.io/api/projects" \\
  -H "Authorization: Token sk_live_xxxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "Chest X-Ray Pneumonia Detection",
    "annotation_type": "bounding_box",
    "labels": ["Bacterial Pneumonia", "Viral Pneumonia", "Normal"]
  }'`,
        },
      },
    ],
  },
  {
    id: "tasks_sdk",
    category: "SDK REFERENCE",
    name: "Tasks & Annotations",
    description: "Query task progress, doctor annotations, and AI pre-labeling predictions.",
    methods: [
      {
        id: "tasks_list",
        name: "client.tasks.list",
        httpMethod: "GET",
        endpoint: "/api/tasks",
        signature: "client.tasks.list(project_id, only_annotated=True)",
        description: "List tasks for a project with full annotation results and reviewer metadata.",
        parameters: [
          { name: "project_id", type: "int", required: true, description: "Target Project ID" },
          { name: "only_annotated", type: "bool", required: false, default: "False", description: "Filter only completed tasks" },
        ],
        returns: "SyncPager[Task]",
        examples: {
          python: `# Retrieve completed annotations
for task in client.tasks.list(project_id=123, only_annotated=True):
    print(f"Task {task.id}: {len(task.annotations)} annotations verified")`,
          curl: `curl -X GET "https://app.cynaps.io/api/tasks?project=123&only_annotated=true" \\
  -H "Authorization: Token sk_live_xxxx"`,
        },
      },
    ],
  },
  {
    id: "exports_sdk",
    category: "SDK REFERENCE",
    name: "Exporting Data",
    description: "Export annotations into COCO, NIfTI masks, YOLO, Pascal VOC, or Pandas DataFrames.",
    methods: [
      {
        id: "export_coco",
        name: "client.projects.exports.as_json",
        signature: "client.projects.exports.as_json(project_id, format='coco')",
        description: "Export project annotations directly as structured JSON or COCO polygon dataset.",
        parameters: [
          { name: "project_id", type: "int", required: true, description: "Target Project ID" },
          { name: "format", type: "str", required: false, default: "'json'", description: "'json', 'coco', 'yolo', 'pascal_voc', 'nifti'" },
        ],
        returns: "Dict / JSON dataset with annotations and metadata",
        examples: {
          python: `# Export as COCO annotations
annotations = client.projects.exports.as_json(project_id=123, format="coco")

# Or export straight into a pandas DataFrame
df = client.projects.exports.as_pandas(project_id=123)
print(df.head())`,
          curl: `curl -X GET "https://app.cynaps.io/api/projects/123/export?export_type=COCO" \\
  -H "Authorization: Token sk_live_xxxx" \\
  -o "annotations_coco.json"`,
        },
      },
    ],
  },
  {
    id: "webhooks_sdk",
    category: "SDK REFERENCE",
    name: "Webhooks & Events",
    description: "Configure webhook notifications when annotations or project batches complete.",
    methods: [
      {
        id: "create_webhook",
        name: "client.webhooks.create",
        httpMethod: "POST",
        endpoint: "/api/webhooks",
        signature: "client.webhooks.create(project_id, url, actions)",
        description: "Register a webhook endpoint to receive real-time POST notifications on annotation events.",
        parameters: [
          { name: "project_id", type: "int", required: true, description: "Target project ID (or None for all)" },
          { name: "url", type: "str", required: true, description: "Your HTTPS webhook receiver URL" },
          { name: "actions", type: "list[str]", required: true, description: "['TASK_ANNOTATED', 'PROJECT_COMPLETED']" },
        ],
        returns: "Webhook object",
        examples: {
          python: `webhook = client.webhooks.create(
    project_id=123,
    url="https://api.yourcompany.com/webhooks/cynaps",
    actions=["TASK_ANNOTATED", "PROJECT_COMPLETED"]
)
print(f"Webhook active with ID: {webhook.id}")`,
          curl: `curl -X POST "https://app.cynaps.io/api/webhooks" \\
  -H "Authorization: Token sk_live_xxxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "project": 123,
    "url": "https://api.yourcompany.com/webhooks/cynaps",
    "send_for_actions": ["TASK_ANNOTATED", "PROJECT_COMPLETED"]
  }'`,
        },
      },
    ],
  },
  {
    id: "specs_tables",
    category: "SPECS & GUIDES",
    name: "Rate Limits & Quotas",
    description: "Reference tables for API rate limits, error codes, and quotas.",
    methods: [
      {
        id: "limits_table",
        name: "Rate Limits & Quotas",
        signature: "Standard: 1,000 req/min | Enterprise: 10,000 req/min",
        description: "API requests are subject to rolling per-minute limits per organization.",
        examples: {
          python: `# Rate limits are returned in HTTP headers
# X-RateLimit-Limit: 1000
# X-RateLimit-Remaining: 985
# X-RateLimit-Reset: 1724985600`,
          curl: `curl -I "https://app.cynaps.io/api/projects" \\
  -H "Authorization: Token sk_live_xxxx"`,
        },
      },
    ],
  },
];

// Interactive Method Card Component
const MethodBlock: React.FC<{ method: DocMethod }> = ({ method }) => {
  const [lang, setLang] = useState<"python" | "curl" | "typescript">("python");

  const availableLangs = useMemo(() => {
    const list: ("python" | "curl" | "typescript")[] = ["python"];
    if (method.examples.curl) list.push("curl");
    if (method.examples.typescript) list.push("typescript");
    return list;
  }, [method.examples]);

  const activeCode = method.examples[lang] || method.examples.python;

  return (
    <div className="doc-method-card" id={method.id}>
      <div className="method-header-row">
        <div className="method-title-left">
          {method.httpMethod && (
            <span className={`http-badge ${method.httpMethod.toLowerCase()}`}>{method.httpMethod}</span>
          )}
          <h3 className="method-name-heading">{method.name}</h3>
        </div>
        {method.endpoint && <code className="method-endpoint-tag">{method.endpoint}</code>}
      </div>

      <div className="signature-pill-container">
        <code className="signature-pill">{method.signature}</code>
      </div>

      <p className="method-desc-text">{method.description}</p>

      {method.tip && (
        <div className="clinical-tip-callout">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" className="tip-svg-icon">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <div className="tip-content">
            <span className="tip-title">NOTE</span>
            <p className="tip-desc">{method.tip}</p>
          </div>
        </div>
      )}

      {/* Parameter Table */}
      {method.parameters && method.parameters.length > 0 && (
        <div className="params-container">
          <h4 className="params-heading">Parameters</h4>
          <div className="params-table-wrapper">
            <table className="params-table">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {method.parameters.map((param, idx) => (
                  <tr key={idx}>
                    <td>
                      <code className="param-name">{param.name}</code>
                    </td>
                    <td>
                      <span className="param-type-badge">{param.type}</span>
                    </td>
                    <td>
                      {param.required ? (
                        <span className="param-status-badge required">Required</span>
                      ) : (
                        <span className="param-status-badge optional">Optional</span>
                      )}
                    </td>
                    <td className="param-desc-cell">
                      <span>{param.description}</span>
                      {param.default && <span className="param-default">Default: {param.default}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Code Snippet Box with Tabs */}
      <div className="code-box-container">
        <div className="code-box-header">
          <div className="lang-tabs-group">
            {availableLangs.map((l) => (
              <button
                key={l}
                type="button"
                className={`lang-tab-btn ${lang === l ? "active" : ""}`}
                onClick={() => setLang(l)}
              >
                {l === "python" ? "Python SDK" : l === "curl" ? "cURL" : "TypeScript"}
              </button>
            ))}
          </div>
          <CopyButton text={activeCode} />
        </div>
        <pre className="code-box-pre">
          <code>{activeCode}</code>
        </pre>
      </div>

      {/* Response Payload Example if present */}
      {method.responseExample && (
        <div className="response-box-container">
          <div className="response-box-header">
            <span className="response-label">Response 200 OK</span>
            <CopyButton text={method.responseExample} />
          </div>
          <pre className="code-box-pre response-pre">
            <code>{method.responseExample}</code>
          </pre>
        </div>
      )}
    </div>
  );
};

export const ApiDocsPage: Page = () => {
  const [activeSectionId, setActiveSectionId] = useState<string>("overview");
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Group sections by category
  const categories = useMemo(() => {
    const map = new Map<string, DocSection[]>();
    docSections.forEach((section) => {
      if (!map.has(section.category)) {
        map.set(section.category, []);
      }
      map.get(section.category)!.push(section);
    });
    return Array.from(map.entries());
  }, []);

  // Filter sections based on search query
  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return docSections;
    const q = searchQuery.toLowerCase();
    return docSections.filter((s) => {
      const matchSection = s.name.toLowerCase().includes(q) || s.description.toLowerCase().includes(q);
      const matchMethod = s.methods.some(
        (m) =>
          m.name.toLowerCase().includes(q) ||
          m.signature.toLowerCase().includes(q) ||
          m.description.toLowerCase().includes(q)
      );
      return matchSection || matchMethod;
    });
  }, [searchQuery]);

  const activeSection = docSections.find((s) => s.id === activeSectionId) || docSections[0];

  // Scroll to top upon section switch
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [activeSectionId]);

  return (
    <div className="cynaps-docs-page">
      {/* Top Header */}
      <header className="docs-top-hero">
        <div className="docs-hero-inner">
          <span className="docs-tag">// DEVELOPER DOCUMENTATION</span>
          <h1 className="docs-hero-title">Cynaps Platform & SDK</h1>
          <p className="docs-hero-subtitle">
            Integrate independent validation into your ML pipeline. Stream DICOM scans, run multi-reviewer consensus, and programmatically export audit-ready quality reports.
          </p>

          {/* Quick Badges */}
          <div className="docs-hero-actions">
            <div className="quick-install-pill">
              <span className="prompt-sym">$</span>
              <code>pip install cynaps-sdk</code>
              <CopyButton text="pip install cynaps-sdk" />
            </div>

            <a
              href="/docs/api/schema/swagger-ui/"
              target="_blank"
              rel="noopener noreferrer"
              className="docs-secondary-btn"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              <span>REST Swagger API</span>
            </a>
          </div>
        </div>
      </header>

      {/* Main Documentation Two-Column Layout */}
      <div className="docs-main-layout">
        {/* Sticky Sidebar */}
        <aside className="docs-sidebar">
          {/* Live Search Input */}
          <div className="docs-search-wrapper">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search docs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="docs-search-input"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery("")} className="search-clear-btn">
                ✕
              </button>
            )}
          </div>

          {/* Categorized Navigation List */}
          <nav className="docs-nav-categories">
            {categories.map(([category, sections]) => {
              const visibleSections = sections.filter((s) => filteredSections.some((fs) => fs.id === s.id));
              if (visibleSections.length === 0) return null;

              return (
                <div key={category} className="nav-category-group">
                  <span className="nav-category-title">// {category}</span>
                  <div className="nav-items-list">
                    {visibleSections.map((sec) => (
                      <button
                        key={sec.id}
                        type="button"
                        onClick={() => {
                          setActiveSectionId(sec.id);
                          setSearchQuery("");
                        }}
                        className={`nav-item-btn ${activeSectionId === sec.id ? "active" : ""}`}
                      >
                        <span className="nav-item-name">{sec.name}</span>
                        {sec.badge && <span className="nav-item-badge">{sec.badge}</span>}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </nav>
        </aside>

        {/* Content Panel */}
        <main className="docs-content-pane">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeSection.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="section-content-wrapper"
            >
              {/* Breadcrumb & Section Header */}
              <div className="section-head-card">
                <div className="section-breadcrumb">
                  <span>Docs</span>
                  <span>/</span>
                  <span>{activeSection.category}</span>
                  <span>/</span>
                  <span className="active-leaf">{activeSection.name}</span>
                </div>
                <h2 className="active-section-title">{activeSection.name}</h2>
                <p className="active-section-desc">{activeSection.description}</p>
              </div>

              {/* Methods List */}
              <div className="methods-flow-list">
                {activeSection.methods.map((method) => (
                  <MethodBlock key={method.id} method={method} />
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
};

ApiDocsPage.title = "API Documentation";
ApiDocsPage.path = "/docs";
ApiDocsPage.exact = true;

export default ApiDocsPage;
